/**
 * `useMemed` — abre a prescrição digital Memed de qualquer tela do prontuário.
 *
 * O ciclo de vida do módulo Memed é global (script único por sessão, listeners
 * registrados uma vez), então a máquina de estados vive no escopo do módulo e o
 * hook apenas assina os eventos e expõe o estado para a UI.
 *
 * @see https://doc.memed.com.br/docs/boas-praticas-integracao/
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MEMED_MODULE,
  MemedPatientPayload,
  MemedPrefillItem,
  MemedPrescriptionContext,
  MemedSession,
  MemedStatus,
  createMemedSession,
  fetchMemedPatientPayload,
  fetchMemedStatus,
  loadMemedScript,
  memedErrorMessage,
  notifyMemedPrescriptionDeleted,
  saveMemedPrescription,
} from '../services/memedService';
import { openMemedPrescriptionWorkspace } from '../utils/memedWorkspace';
import { notifyMemedPrescriptionSaved } from '../utils/memedWorkspaceChannel';

// ---------------------------------------------------------------------------
// Estado global do módulo Memed
// ---------------------------------------------------------------------------

interface SavedEvent {
  ownerId: string;
  prescription: any;
  reprinted: boolean;
}

const savedSubscribers = new Set<(event: SavedEvent) => void>();
const failureSubscribers = new Set<(event: { ownerId: string; message: string }) => void>();
const closeSubscribers = new Set<(ownerId: string) => void>();

/** Contexto clínico do atendimento aberto agora — usado ao persistir o evento. */
let activeContext: MemedPrescriptionContext = {};
let activeOwnerId = '';

let businessEventsBound = false;
let moduleLifecycleBound = false;
let modulePreparation: { token: string; promise: Promise<void> } | null = null;
let statusPromise: Promise<MemedStatus> | null = null;

/** `MdHub.module.show/hide` do SDK legado retornam `undefined`, não promise. */
function toPromise<T>(value: PromiseLike<T> | T): Promise<T> {
  return value && typeof (value as PromiseLike<T>).then === 'function'
    ? Promise.resolve(value as PromiseLike<T>)
    : Promise.resolve(value as T);
}

function withTimeout<T>(value: PromiseLike<T> | T, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    toPromise(value).then(
      (result) => {
        window.clearTimeout(timer);
        resolve(result);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Sinal de que `plataforma.prescricao` inicializou. O SDK legado carrega os
 * módulos em sequência (~10 s a 20 s) e **descarta silenciosamente** qualquer
 * comando enviado antes disso: a promise nunca resolve nem rejeita. Por isso a
 * preparação espera este sinal antes de falar com o módulo.
 */
let markModuleReady: () => void = () => undefined;
const moduleReady: Promise<void> = new Promise<void>((resolve) => {
  markModuleReady = resolve;
});

/** `core:ping` só responde depois de `core:moduleInit`; antes, fica pendente. */
function pingOnce(MdHub: NonNullable<Window['MdHub']>, ms: number): Promise<boolean> {
  const ping = MdHub.command.ping;
  if (typeof ping !== 'function') return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const timer = window.setTimeout(() => resolve(false), ms);
    toPromise(ping.call(MdHub.command, MEMED_MODULE)).then(
      () => {
        window.clearTimeout(timer);
        resolve(true);
      },
      () => {
        window.clearTimeout(timer);
        resolve(false);
      },
    );
  });
}

/**
 * Só devolve quando o módulo de prescrição responde de fato. O evento
 * `core:moduleInit` acelera a espera; o ping é o que confirma — e cobre o caso
 * em que o módulo já havia inicializado antes de registrarmos os listeners.
 */
async function waitForModuleReady(MdHub: NonNullable<Window['MdHub']>, timeoutMs: number) {
  // O bridge V4 não expõe `ping`: nele os comandos ficam enfileirados até o
  // iframe sinalizar MEMED_READY, então não há o que esperar aqui.
  if (typeof MdHub.command.ping !== 'function') return;

  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (await pingOnce(MdHub, 2500)) return;
    await Promise.race([moduleReady, sleep(1200)]);
  }

  throw new Error(
    'O módulo de prescrição da Memed não terminou de inicializar. ' +
      'Recarregue a página e tente novamente.',
  );
}

function getStatusOnce(force = false): Promise<MemedStatus> {
  if (!statusPromise || force) {
    statusPromise = fetchMemedStatus().catch((error) => {
      statusPromise = null;
      throw error;
    });
  }
  return statusPromise;
}

/**
 * `prescricaoImpressa` e `prescricaoExcluida` são critério de homologação da
 * Memed: sem persistir, a receita só existiria do lado deles.
 */
function bindBusinessEvents(MdHub: NonNullable<Window['MdHub']>) {
  if (businessEventsBound) return;
  businessEventsBound = true;

  MdHub.event.add('prescricaoImpressa', async (payload: any) => {
    const ownerId = activeOwnerId;
    const context = { ...activeContext };
    try {
      const prescription = await saveMemedPrescription(payload, context);
      const reprinted = Boolean(prescription?.reprinted);
      if (context.patient_id) {
        notifyMemedPrescriptionSaved({
          patientId: String(context.patient_id),
          prescription,
          reprinted,
        });
      }
      savedSubscribers.forEach((fn) =>
        fn({ ownerId, prescription, reprinted }),
      );
    } catch (error: any) {
      const message = memedErrorMessage(
        error,
        'A receita foi emitida na Memed, mas não foi possível salvá-la no prontuário.',
      );
      failureSubscribers.forEach((fn) => fn({ ownerId, message }));
    }
  });

  MdHub.event.add('prescricaoExcluida', async (payload: any) => {
    const memedId = payload?.id ?? payload?.prescricao?.id;
    if (!memedId) return;
    try {
      await notifyMemedPrescriptionDeleted(memedId);
      savedSubscribers.forEach((fn) =>
        fn({ ownerId: activeOwnerId, prescription: null, reprinted: false }),
      );
    } catch {
      // A exclusão será reconciliada na próxima abertura do prontuário.
    }
  });
}

async function applySessionCommands(
  MdHub: NonNullable<Window['MdHub']>,
  session: MemedSession,
) {
  const { workplace, feature_toggles: toggles, additional_data: additional } = session;
  const commands: Promise<unknown>[] = [];

  if (workplace?.local_name || workplace?.cnes) {
    commands.push(
      withTimeout(
        MdHub.command.send(MEMED_MODULE, 'setWorkplace', workplace),
        20000,
        'Timeout ao configurar o local de atendimento.',
      ).catch(() => undefined),
    );
  }
  if (toggles && Object.keys(toggles).length > 0) {
    commands.push(
      withTimeout(
        MdHub.command.send(MEMED_MODULE, 'setFeatureToggle', toggles),
        20000,
        'Timeout ao configurar os recursos da Memed.',
      ).catch(() => undefined),
    );
  }
  if (additional && (additional.header?.length > 0 || additional.footer)) {
    commands.push(
      withTimeout(
        MdHub.command.send(MEMED_MODULE, 'setAdditionalData', additional),
        20000,
        'Timeout ao configurar o cabeçalho da receita.',
      ).catch(() => undefined),
    );
  }

  // No V4 esses comandos aguardam MEMED_READY. Em paralelo, a preparação não
  // acumula três timeouts consecutivos quando um recurso opcional está lento.
  await Promise.all(commands);
}

function bindModuleLifecycle(runtime: {
  MdSinapsePrescricao: NonNullable<Window['MdSinapsePrescricao']>;
  MdHub: NonNullable<Window['MdHub']>;
}) {
  if (moduleLifecycleBound) return;
  moduleLifecycleBound = true;

  // Registrado antes de qualquer comando: no SDK legado estes listeners são
  // enfileirados e reproduzidos quando o hub inicializa os módulos.
  runtime.MdSinapsePrescricao.event.add('core:moduleInit', (module: any) => {
    if (module?.name !== MEMED_MODULE) return;
    markModuleReady();
    bindBusinessEvents(runtime.MdHub);
  });
  runtime.MdSinapsePrescricao.event.add('core:onready', (module: any) => {
    // O payload de `core:onready` é o nome do módulo (string).
    if (module !== MEMED_MODULE && module?.name !== MEMED_MODULE) return;
    markModuleReady();
  });
  runtime.MdSinapsePrescricao.event.add('core:moduleHide', (module: any) => {
    if (module?.moduleName !== MEMED_MODULE) return;
    closeSubscribers.forEach((fn) => fn(activeOwnerId));
  });
}

/**
 * Garante script carregado + módulo inicializado + comandos de sessão aplicados.
 * Reaproveita a preparação enquanto o token do prescritor não mudar.
 */
function prepareModule(session: MemedSession): Promise<void> {
  if (modulePreparation && modulePreparation.token === session.token) {
    return modulePreparation.promise;
  }

  const promise = (async () => {
    const runtime = await loadMemedScript(session);
    // Os listeners precisam existir antes do hub inicializar os módulos.
    bindModuleLifecycle(runtime);

    await waitForModuleReady(runtime.MdHub, 75000);

    // Se o módulo já estava inicializado antes dos listeners, `core:moduleInit`
    // não passa por aqui — então os eventos de negócio são ligados também agora.
    bindBusinessEvents(runtime.MdHub);

    // Toggles/cabeçalho são personalização: não impedem prescrever.
    await applySessionCommands(runtime.MdHub, session);
  })();

  modulePreparation = { token: session.token, promise };
  promise.catch(() => {
    modulePreparation = null;
  });
  return promise;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseMemedOptions {
  /** Prescritor padrão; pode ser sobrescrito na chamada de `openPrescription`. */
  professionalId?: string | null;
  /** Chamado após a receita ser persistida no Klinvia (bom para dar refresh). */
  onPrescriptionSaved?: (prescription: any, meta: { reprinted: boolean }) => void;
}

export interface OpenPrescriptionParams {
  patientId: string;
  professionalId?: string | null;
  appointmentId?: string | null;
  medicalRecordId?: string | null;
  /** Renovação: itens da receita anterior, adicionados via `addItem`. */
  items?: MemedPrefillItem[];
}

export type MemedDisplayMode = 'newTab' | 'inline';

export interface OpenMemedPrescriptionOptions {
  /** `newTab` (padrão): não bloqueia o Klinvia. `inline`: módulo em tela cheia nesta aba. */
  display?: MemedDisplayMode;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * `addItem` com o id da base Memed traz o medicamento completo (alertas,
 * receituário); sem id — ou se a Memed recusar o id — entra como texto livre.
 */
async function prefillItems(MdHub: NonNullable<Window['MdHub']>, items: MemedPrefillItem[]): Promise<number> {
  let added = 0;
  for (const item of items) {
    const name = String(item.medication_name || '').trim();
    if (!name) continue;
    const posology = String(item.instructions || item.dosage || '').trim();
    const quantity = Number(item.quantity_numeric);
    const base: Record<string, unknown> = {
      nome: name,
      posologia: posology ? `<p>${escapeHtml(posology).replace(/\n/g, '<br>')}</p>` : '',
      quantidade: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };
    const canUseCatalogId = item.memed_item_id && !/custom|livre/i.test(String(item.memed_item_type || ''));
    const attempts = canUseCatalogId ? [{ ...base, id: item.memed_item_id }, base] : [base];
    for (const params of attempts) {
      try {
        await withTimeout(MdHub.command.send(MEMED_MODULE, 'addItem', params), 15000, 'addItem');
        added += 1;
        break;
      } catch {
        // Tenta como texto livre.
      }
    }
  }
  return added;
}

let ownerSeq = 0;

export function useMemed(options: UseMemedOptions = {}) {
  const { professionalId, onPrescriptionSaved } = options;

  const ownerId = useMemo(() => `memed-owner-${++ownerSeq}`, []);
  const savedCallbackRef = useRef(onPrescriptionSaved);
  savedCallbackRef.current = onPrescriptionSaved;

  const [status, setStatus] = useState<MemedStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getStatusOnce()
      .then((data) => {
        if (alive) setStatus(data);
      })
      .catch(() => {
        if (alive) setStatus(null);
      })
      .finally(() => {
        if (alive) setStatusLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onSaved = (event: SavedEvent) => {
      if (event.ownerId !== ownerId) return;
      if (event.prescription) {
        setNotice(
          event.reprinted
            ? 'Receita Memed atualizada no prontuário.'
            : 'Receita Memed emitida e salva no prontuário.',
        );
      }
      savedCallbackRef.current?.(event.prescription, { reprinted: event.reprinted });
    };
    const onFailure = (event: { ownerId: string; message: string }) => {
      if (event.ownerId !== ownerId) return;
      setError(event.message);
    };
    const onClose = (closedOwnerId: string) => {
      if (closedOwnerId !== ownerId) return;
      setOpening(false);
    };

    savedSubscribers.add(onSaved);
    failureSubscribers.add(onFailure);
    closeSubscribers.add(onClose);
    return () => {
      savedSubscribers.delete(onSaved);
      failureSubscribers.delete(onFailure);
      closeSubscribers.delete(onClose);
    };
  }, [ownerId]);

  const openPrescription = useCallback(
    async (params: OpenPrescriptionParams, options: OpenMemedPrescriptionOptions = {}) => {
      if (!params?.patientId) {
        setError('Selecione o paciente antes de abrir a prescrição digital.');
        return false;
      }

      const display = options.display ?? 'newTab';
      if (display === 'newTab') {
        setError(null);
        const opened = openMemedPrescriptionWorkspace({
          patientId: params.patientId,
          professionalId: params.professionalId || professionalId || null,
          appointmentId: params.appointmentId,
          medicalRecordId: params.medicalRecordId,
          items: params.items,
        });
        if (!opened) {
          setError(
            'Não foi possível abrir a Memed em nova aba. Permita pop-ups para este site e tente novamente.',
          );
          return false;
        }
        return true;
      }

      setOpening(true);
      setError(null);
      try {
        const resolvedProfessionalId = params.professionalId || professionalId || null;

        // Token do prescritor não é estático: buscamos um novo a cada abertura.
        const [session, patient] = await Promise.all([
          createMemedSession(resolvedProfessionalId),
          fetchMemedPatientPayload(params.patientId),
        ]);

        await withTimeout(
          prepareModule(session),
          90000,
          'O módulo Memed demorou demais para carregar. Verifique sua conexão e tente novamente.',
        );

        const MdHub = window.MdHub;
        if (!MdHub) throw new Error('Módulo da Memed indisponível. Recarregue a página.');

        activeOwnerId = ownerId;
        activeContext = {
          professional_id: session.professional.id,
          patient_id: params.patientId,
          appointment_id: params.appointmentId || null,
          medical_record_id: params.medicalRecordId || null,
        };

        await withTimeout(
          MdHub.command.send(MEMED_MODULE, 'setPaciente', patient as MemedPatientPayload),
          20000,
          'Não foi possível enviar os dados do paciente para a Memed.',
        );
        // `module.show` do SDK legado é síncrono e devolve `undefined`.
        await withTimeout(
          MdHub.module.show(MEMED_MODULE),
          45000,
          'A Memed não abriu no tempo esperado. Feche bloqueadores de pop-up, recarregue a página e tente novamente.',
        );

        if (params.items && params.items.length > 0) {
          const added = await prefillItems(MdHub, params.items);
          setNotice(
            added === params.items.length
              ? `${added} item(ns) da receita anterior carregado(s) na Memed. Revise e assine.`
              : `${added} de ${params.items.length} itens carregados na Memed. Complete os que faltaram.`,
          );
        }

        // O módulo Memed tem UI própria; não manter o botão em "Abrindo…" até o fechamento.
        setOpening(false);
        return true;
      } catch (err: any) {
        modulePreparation = null;
        setError(memedErrorMessage(err));
        setOpening(false);
        return false;
      }
    },
    [ownerId, professionalId],
  );

  /**
   * Começa a carregar o SDK assim que o prescritor é conhecido. O boot do SDK
   * legado leva de 10 s a 20 s; adiantá-lo faz o clique em "abrir prescrição"
   * responder de imediato. Falhas aqui são silenciosas — quem reporta erro é
   * `openPrescription`.
   */
  const warmUp = useCallback(
    async (targetProfessionalId?: string | null) => {
      const resolved = targetProfessionalId || professionalId || null;
      if (!resolved) return;
      try {
        const session = await createMemedSession(resolved);
        await prepareModule(session);
      } catch {
        // Silencioso: o usuário ainda não pediu para abrir a prescrição.
      }
    },
    [professionalId],
  );

  const closePrescription = useCallback(async () => {
    try {
      await window.MdHub?.module.hide(MEMED_MODULE);
    } catch {
      // Módulo já fechado.
    }
    setOpening(false);
  }, []);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      setStatus(await getStatusOnce(true));
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  return {
    status,
    statusLoading,
    /** Só renderize o botão da Memed quando `available` for verdadeiro. */
    available: Boolean(status?.enabled),
    environment: status?.environment ?? null,
    opening,
    error,
    notice,
    clearError: useCallback(() => setError(null), []),
    clearNotice: useCallback(() => setNotice(null), []),
    openPrescription,
    /** Opcional: chame ao escolher o prescritor para adiantar o boot do SDK. */
    warmUp,
    closePrescription,
    refreshStatus,
  };
}

export default useMemed;
